import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  OutletFulfilmentStateType,
  useOutletFulfilment,
} from '@src/hooks/outletFulfilmentAndBasketHooks/useOutletFulfilment/useOutletFulfilment'
import {
  CheckoutRoute,
  useCheckoutRouter,
} from '@src/hooks/useCheckoutRouter/useCheckoutRouter'
import { useBasketTotalsContext } from '@src/hooks/useTotals/BasketTotalsContext'

import {
  CheckoutRewardsContainer,
  DealsAndOffersContainer,
  DealsAndOffersHeader,
  HeaderAndMinSpendContainer,
  MinimumSpendNeededContainer,
  MinSpendText,
  MinSpendTextBold,
} from './CheckoutRewards.styles'

import { useFormatCurrency } from '@src/hooks/useFormatCurrency'

import { Discount } from '@src/graphql-types'

import { useAllDiscountsQuery } from '@src/hooks/sharedQueries/useAllDiscounts/useAllDiscounts'
import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { useNavigate } from 'react-router-dom'
import { useMarketplace } from '@src/hooks/useMarketplace'
import { convertToSlug } from '@src/utils/convertToSlug'
import { LoyaltyCardDealsSection } from './LoyaltyCardDealsSection'
import { AddOnItemsDealsSection } from './AddOnItemsDealsSection'
import { DiscountCodeDealsSection } from './DiscountCodeDealsSection'
import { DiscountDealsSection } from './DiscountDealsSection'

export const CheckoutRewards: React.FC<{
  basketSubTotal: number
  outletId: string
  appliedDiscount: Pick<Discount, 'id' | 'name'> | null
}> = ({ basketSubTotal }) => {
  const { t } = useTranslation('customerLoyaltyCard')
  const { basketTotals } = useBasketTotalsContext()

  const outletFulfilment = useOutletFulfilment({
    stateType: OutletFulfilmentStateType.GLOBAL,
  })

  const checkout = useCheckoutRouter()
  const formatCurrency = useFormatCurrency()
  const navigate = useNavigate()
  const { urlPath } = useMarketplace()

  const { data: discountData, loading: discountsLoading } =
    useAllDiscountsQuery({
      outletId: outletFulfilment.outlet.id,
      fulfilmentNarrowType: outletFulfilment.data.currentFulfilment.narrowType,
    })

  const cardsData = basketTotals?.data?.checkoutBasketTotals?.loyaltyCards
    ?.length
    ? basketTotals?.data?.checkoutBasketTotals?.loyaltyCards
    : outletFulfilment.outlet.outletLoyaltyCards
  const hasVoucherDiscount = Boolean(
    basketTotals.data?.checkoutBasketTotals?.appliedDiscount?.discount?.voucher
  )
  const hasAddOnItems = Boolean(
    basketTotals.data?.checkoutBasketTotals?.addOnItemsTotals.length
  )
  const fulfilmentType =
    outletFulfilment.data.currentFulfilment.narrowTypeLowerCase
  const isTableService = fulfilmentType === 'table'
  const minAmountForOffers =
    basketTotals?.data?.checkoutBasketTotals?.minimumSpendToEarnOffers ?? 0

  const isBasketView =
    checkout.route === CheckoutRoute.BASKET || checkout.route === undefined
  const hasLoyaltyCardDeals = !!(cardsData && cardsData.length)
  // For Table Service: only show if there are add-on items or loyatly cards
  // For Delivery/Collection: always show (to allow discount code input)
  if (isTableService && (!hasAddOnItems || !hasLoyaltyCardDeals)) {
    return null
  }

  return (
    <DealsAndOffersContainer
      isBasket={isBasketView}
      data-test-id="checkout-container-dealsandoffers"
    >
      {discountsLoading || basketTotals.loading ? (
        <LoadingSpinner data-test-id="checkout-spinner-loading" />
      ) : (
        <>
          <HeaderAndMinSpendContainer
            isBasket={isBasketView}
            data-test-id="checkout-container-header"
          >
            <DealsAndOffersHeader
              isBasket={isBasketView}
              data-test-id="checkout-text-dealsandoffers"
            >
              {t('deals_and_offers')}
            </DealsAndOffersHeader>

            {minAmountForOffers > 0 && isBasketView && (
              <MinimumSpendNeededContainer
                onClick={() =>
                  navigate(
                    `/${urlPath}/${outletFulfilment.outlet.id}/${convertToSlug(
                      outletFulfilment.outlet.displayName
                    )}/menu`,
                    { replace: true }
                  )
                }
                data-test-id="checkout-button-minimumspend"
              >
                <MinSpendText>
                  {t('add')}
                  <MinSpendTextBold>
                    {formatCurrency(minAmountForOffers)}
                  </MinSpendTextBold>
                  {t('to_start_earning_offers')}
                </MinSpendText>
              </MinimumSpendNeededContainer>
            )}
          </HeaderAndMinSpendContainer>

          <CheckoutRewardsContainer
            isBasket={isBasketView}
            data-test-id="checkout-container-rewards"
          >
            {!isTableService && (
              <LoyaltyCardDealsSection cardsData={cardsData} />
            )}
            {!isTableService && !hasVoucherDiscount && (
              <DiscountDealsSection
                basketSubTotal={basketSubTotal}
                discountData={discountData}
              />
            )}
            <AddOnItemsDealsSection />
            {!isTableService && <DiscountCodeDealsSection />}
          </CheckoutRewardsContainer>
        </>
      )}
    </DealsAndOffersContainer>
  )
}
